import { createFileRoute } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { sessionQueryOptions, useDeviceTimezoneSync, useSession } from '@/entities/session'
import { BrandLogo } from '@/shared/ui/brand'
import { Button } from '@/shared/ui/button'
import { Spinner } from '@/shared/ui/spinner'
import { AppShell } from '@/widgets/app-shell'
import { CommandPalette } from '@/widgets/command-palette'

// Гейт всей аутентифицированной зоны: сессия из /api/employees/me. Аноним уходит
// на единый вход экосистемы /login (22-unified-login), а не сразу в Authentik:
// точка входа одна на обе аудитории, и заказчик, ткнувший в сотрудническую
// ссылку, получает объяснение и свою форму вместо тупика в чужом IdP.
// Сотрудник со второго раза уезжает в Authentik автоматически — по запомненному
// способу. Текущий адрес едет в ?rd=, чтобы вернуть ровно туда, куда шли.
export const Route = createFileRoute('/_app')({
  loader: ({ context }) => context.queryClient.ensureQueryData(sessionQueryOptions),
  component: AppLayout,
  pendingComponent: SessionPending,
  errorComponent: SessionError,
})

// Брендированный полноэкранный статус — переходные/терминальные состояния входа.
function AuthScreen({ message, children }: { message: string; children?: ReactNode }) {
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-bg-base px-4 py-12 text-text-primary">
      <div className="flex flex-col items-center gap-4 text-center">
        <BrandLogo className="h-7" />
        <div>
          <h1 className="t-h3">STIT Hub</h1>
          <p className="mt-1 text-sm text-text-tertiary">
            Компании, проекты и сотрудники экосистемы
          </p>
        </div>
      </div>
      <div className="flex flex-col items-center gap-4 text-center">
        <p className="text-sm text-text-secondary">{message}</p>
        {children}
      </div>
    </main>
  )
}

function SessionPending() {
  return (
    <AuthScreen message="Проверяем сессию…">
      <Spinner label="Проверяем сессию" />
    </AuthScreen>
  )
}

// Сессию проверить не удалось (сеть/бэкенд, не 401) — даём переиграть, не
// зацикливая редирект на SSO.
function SessionError() {
  return (
    <AuthScreen message="Не удалось проверить сессию. Проверьте сеть и попробуйте ещё раз.">
      <Button onClick={() => window.location.reload()}>Повторить</Button>
    </AuthScreen>
  )
}

function AppLayout() {
  const { session, isLoading } = useSession()
  const navigate = Route.useNavigate()

  // Зона устройства едет в хаб на загрузке сессии — молча, без спроса: сохранять
  // её как настройку нельзя (протухнет в первом же перелёте), а серверу она
  // нужна там, где экрана нет, — в тексте письма и алерта бота.
  useDeviceTimezoneSync()

  // Эффектом, а не redirect'ом в loader'е: 401 посреди работы гасит сессию в
  // кэше (setUnauthorizedHandler), и уводить надо тем же путём, что аноним.
  useEffect(() => {
    if (isLoading || session) return
    void navigate({ to: '/login', search: { rd: window.location.href }, replace: true })
  }, [isLoading, session, navigate])

  // Аноним: переход на /login уже запущен; экран — брендированная прослойка
  // с ручным запасным входом (если навигация почему-то не сработала).
  if (!session) {
    return (
      <AuthScreen message="Переходим ко входу…">
        <Spinner label="Переход к входу" />
        <Button
          onClick={() => {
            void navigate({ to: '/login', search: { rd: window.location.href } })
          }}
        >
          Войти
        </Button>
      </AuthScreen>
    )
  }

  return (
    <>
      <AppShell />
      <CommandPalette />
    </>
  )
}
